import { ReactElement, useMemo, useState } from 'react'
import styled from 'styled-components'
import TableHeaders from './components/TableHeader'
import { ITableInfinite } from './InfiniteTable.interfaces'
import { InfiniteTableProvider, useInfiniteTableContext } from './InfiniteTable.context'
import TableContent from './components/TableContent'
import { Theme } from 'theme/theme'
import CsvDownloadButton from 'react-json-to-csv'
import { Button, CircularProgress } from '@mui/material'

export default function TableInfinite<T>(input: Readonly<ITableInfinite<T>>): ReactElement {
  const { isLoading, exportFileName, exportFields, data } = input
  const [isLoadingAll, setIsLoadingAll] = useState(false)

  const getAll = () => {
    setIsLoadingAll(true)
    input.getAll()
  }

  const exportData = useMemo(() => {
    if (!exportFields) return data

    return data.map((row, index) => {
      const newRow: Record<string, unknown> = {}
      exportFields.forEach((field) => {
        newRow[field.label] = field.format ? field.format(row, false, index) : row[field.indexName]
      })
      return newRow
    })
  }, [data, exportFields])

  return (
    <InfiniteTableProvider input={input}>
      <Wrapper>
        {exportFileName && input.isQueriedAllCompleted ? (
          <CsvDownloadButton
            filename={exportFileName}
            data={exportData}
            style={{ display: 'flex', marginBottom: 15, padding: 5, width: 400, justifyContent: 'center' }}
          >
            Télécharger - {exportFileName}
          </CsvDownloadButton>
        ) : null}
        {input.getAll && !input.isQueriedAllCompleted ? (
          <Button
            onClick={getAll}
            style={{ display: 'flex', marginBottom: 15, padding: 5, width: 400, justifyContent: 'center' }}
            variant="contained"
          >
            {isLoadingAll ? 'Fichier extraction en préparation...' : 'Extraction des données (.csv)'}
          </Button>
        ) : null}
        <TableHeaders<T> />
        {isLoading ? <CircularProgress style={{ margin: 'auto' }} /> : <ErrorMessage<T> />}
        <TableContent<T> />
      </Wrapper>
    </InfiniteTableProvider>
  )
}

const ErrorMessage = <T,>(): ReactElement => {
  const { displayError, data } = useInfiniteTableContext<T>()

  if (displayError) return <EmptyText>{displayError}</EmptyText>
  if (data.length === 0) return <EmptyText>Aucun résultat trouvé pour cette recherche</EmptyText>

  return null
}

const Wrapper = styled.div<{ height?: number }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  ${(props) => (props.height ? 'max-height: ' + props.height + 'px' : '')};
  height: 100%;
  width: 100%;
`

export const EmptyText = styled.div`
  font-family: ${Theme.font.family};
  ${Theme.font.size.normal};
  font-style: italic;
  text-align: center;
  margin-top: 50px;
`
